import { useCallback, useEffect, useMemo, useState } from 'react';
import { OptionKey } from '@common-fe/common-fe';

import { ICardPurseDtoStatusEnum } from '@/dtos';

import { CardPurseAccess, CardPursePatchPayload } from '../ManageAccountAccessModal.types';

export enum ManageAccountAccessStep {
  Form = 'Form',
  Confirm = 'Confirm',
}

interface Params {
  purses: CardPurseAccess[];
}

export const useManageAccountAccessState = ({ purses }: Params) => {
  const [step, setStep] = useState<ManageAccountAccessStep>(ManageAccountAccessStep.Form);
  const [lockedIds, setLockedIds] = useState<string[]>([]);
  const [employeeUnlockForbiddenIds, setEmployeeUnlockForbiddenIds] = useState<string[]>([]);
  const [initialEmployeeUnlockForbiddenIds, setInitialEmployeeUnlockForbiddenIds] = useState<string[]>([]);
  const [lockReasons, setLockReasons] = useState<Record<string, OptionKey>>({});

  useEffect(() => {
    const forbiddenIds = purses.filter((purse) => purse.employeeUnlockForbidden).map((purse) => purse.id);
    setLockedIds(
      purses.filter((purse) => purse.status === ICardPurseDtoStatusEnum.FROZEN).map((purse) => purse.id)
    );
    setEmployeeUnlockForbiddenIds(forbiddenIds);
    setInitialEmployeeUnlockForbiddenIds(forbiddenIds);
  }, [purses]);

  const toggleLock = useCallback((id: string, locked: boolean) => {
    setLockedIds((prev) => (locked ? [...prev, id] : prev.filter((purseId) => purseId !== id)));
    if (!locked) {
      setEmployeeUnlockForbiddenIds((prev) => prev.filter((purseId) => purseId !== id));
    }
  }, []);

  const setEmployeeUnlockForbidden = useCallback((id: string, forbidden: boolean) => {
    setEmployeeUnlockForbiddenIds((prev) =>
      forbidden ? [...prev, id] : prev.filter((purseId) => purseId !== id)
    );
  }, []);

  const editableLockedIds = useMemo(
    () =>
      lockedIds.filter((id) => {
        const purse = purses.find((item) => item.id === id);
        return purse && !purse.isLockedByHealthPlan;
      }),
    [lockedIds, purses]
  );

  const setAllEmployeeUnlockForbidden = useCallback(
    (forbidden: boolean) => {
      setEmployeeUnlockForbiddenIds((prev) => {
        const withoutEditable = prev.filter((id) => !editableLockedIds.includes(id));
        return forbidden ? [...withoutEditable, ...editableLockedIds] : withoutEditable;
      });
    },
    [editableLockedIds]
  );

  const isAllEmployeeUnlockForbidden = useMemo(
    () =>
      editableLockedIds.length > 0 &&
      editableLockedIds.every((id) => employeeUnlockForbiddenIds.includes(id)),
    [editableLockedIds, employeeUnlockForbiddenIds]
  );

  const newlyLockedPurses = useMemo(
    () =>
      purses.filter(
        (purse) => lockedIds.includes(purse.id) && purse.status !== ICardPurseDtoStatusEnum.FROZEN
      ),
    [purses, lockedIds]
  );

  const newlyForbiddenPursesMissingReason = useMemo(
    () =>
      purses.filter(
        (purse) =>
          purse.status === ICardPurseDtoStatusEnum.FROZEN &&
          employeeUnlockForbiddenIds.includes(purse.id) &&
          !initialEmployeeUnlockForbiddenIds.includes(purse.id) &&
          !purse.lockReason &&
          !purse.isLockedByHealthPlan &&
          !purse.isLockedBySystem
      ),
    [purses, employeeUnlockForbiddenIds, initialEmployeeUnlockForbiddenIds]
  );

  const pursesRequiringLockReason = useMemo(
    () => [...newlyLockedPurses, ...newlyForbiddenPursesMissingReason],
    [newlyLockedPurses, newlyForbiddenPursesMissingReason]
  );

  const changedPurses = useMemo(
    () =>
      purses.filter((purse) => {
        const isLockChanged =
          lockedIds.includes(purse.id) !== (purse.status === ICardPurseDtoStatusEnum.FROZEN);
        const isForbiddenFlagChanged =
          employeeUnlockForbiddenIds.includes(purse.id) !== purse.employeeUnlockForbidden;
        return isLockChanged || isForbiddenFlagChanged;
      }),
    [purses, lockedIds, employeeUnlockForbiddenIds]
  );

  const hasChanges = useMemo(() => changedPurses.length > 0, [changedPurses]);

  const setLockReason = useCallback((id: string, reason: OptionKey) => {
    setLockReasons((prev) => ({ ...prev, [id]: reason }));
  }, []);

  const isLockReasonsValid = useMemo(
    () => pursesRequiringLockReason.every((purse) => !!lockReasons[purse.id]),
    [pursesRequiringLockReason, lockReasons]
  );

  const goToConfirm = useCallback(() => setStep(ManageAccountAccessStep.Confirm), []);
  const goToForm = useCallback(() => setStep(ManageAccountAccessStep.Form), []);
  const reset = useCallback(() => {
    setStep(ManageAccountAccessStep.Form);
    setLockReasons({});
  }, []);

  const buildPayload = useCallback(
    (): CardPursePatchPayload[] =>
      changedPurses.map((purse) => {
        const isLocked = lockedIds.includes(purse.id);
        return {
          id: Number(purse.id),
          status: isLocked ? ICardPurseDtoStatusEnum.FROZEN : ICardPurseDtoStatusEnum.ACTIVE,
          lock_reason: isLocked ? (lockReasons[purse.id] as string) || purse.lockReason : undefined,
          employee_unlock_forbidden: employeeUnlockForbiddenIds.includes(purse.id),
        };
      }),
    [changedPurses, lockedIds, lockReasons, employeeUnlockForbiddenIds]
  );

  return {
    step,
    lockedIds,
    editableLockedIds,
    employeeUnlockForbiddenIds,
    isAllEmployeeUnlockForbidden,
    lockReasons,
    pursesRequiringLockReason,
    hasChanges,
    isLockReasonsValid,
    toggleLock,
    setEmployeeUnlockForbidden,
    setAllEmployeeUnlockForbidden,
    setLockReason,
    goToConfirm,
    goToForm,
    reset,
    buildPayload,
  };
};
